import { getReconnectsLastHour } from "@/lib/xmppReconnectTracker"
import {
  getLocationSubscriptionState,
  type LocationSubscriptionState,
} from "@/lib/locationSubscriptionTracker"

export type SlickshiftDebugSnapshot = {
  takenAt: number
  xmppReconnectsLastHour: number
  locationSubscription: LocationSubscriptionState
  gpsStaleByChat: Record<string, boolean>
}

type Subscriber = (snapshot: SlickshiftDebugSnapshot) => void

let latestGpsStaleByChat: Record<string, boolean> = {}
const subscribers = new Set<Subscriber>()

// Called by the stale-GPS watcher on every tick so the debug snapshot reflects
// the watcher's current per-chat view without us having to walk Redux state.
export const updateGpsStaleByChat = (byChat: Record<string, boolean>): void => {
  latestGpsStaleByChat = { ...byChat }
}

export const slickshiftDebugSnapshot = (
  now: number = Date.now(),
): SlickshiftDebugSnapshot => ({
  takenAt: now,
  xmppReconnectsLastHour: getReconnectsLastHour(now),
  locationSubscription: getLocationSubscriptionState(now),
  gpsStaleByChat: { ...latestGpsStaleByChat },
})

export const notifyDebugSubscribers = (): void => {
  if (subscribers.size === 0) return
  const snap = slickshiftDebugSnapshot()
  for (const sub of subscribers) {
    try {
      sub(snap)
    } catch (e) {
      console.error("[slickshiftDebug] subscriber threw:", e)
    }
  }
}

export const subscribeDebug = (cb: Subscriber): (() => void) => {
  subscribers.add(cb)
  // Fire once immediately so the caller doesn't wait for the next tick or
  // reconnect to see current state.
  try {
    cb(slickshiftDebugSnapshot())
  } catch (e) {
    console.error("[slickshiftDebug] initial-call subscriber threw:", e)
  }
  return () => {
    subscribers.delete(cb)
  }
}

export const __resetSlickshiftDebugForTests = (): void => {
  latestGpsStaleByChat = {}
  subscribers.clear()
}

declare global {
  interface Window {
    slickshiftDebug?: {
      snapshot: () => SlickshiftDebugSnapshot
      subscribe: (cb: Subscriber) => () => void
    }
  }
}

if (typeof window !== "undefined") {
  window.slickshiftDebug = {
    snapshot: () => slickshiftDebugSnapshot(),
    subscribe: subscribeDebug,
  }
}
